import { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useGetConnectedDevicesQuery } from 'services/devices/devices.api';
import FloodSettingsField from '../../../../components/FloodSettingsField/FloodSettingsField';
import { FIELD_HELP, SIMULATION_DISCLAIMER } from '../../config/floodSimConfig';
import type {
  FloodSimSettingsProps,
  PendingDownstreamLink,
  SimSettings,
} from '../../FloodSimSettings.types';
import styles from '../../assets/FloodSimSettings.module.css';
import {
  addUpstream,
  ancestorsOf,
  buildLink,
  DEFAULT_FLOW_SPEED_MPH,
  descendantsOf,
  describeConfluence,
  describeWalk,
  deviceDistanceMiles,
  deviceLabel,
  type DeviceLike,
  downstreamOf,
  isStageDevice,
  removeUpstream,
  setUpstreamSpeed,
  travelMinutes,
  upstreamLinksOf,
  walkUpstream,
  walkUpstreamPaths,
} from '../../utils/floodSimRouting.utils';
import SettingsSection from '../SettingsSection/SettingsSection';

/**
 * WaterwaySection — where this device sits on its waterway, drawn as a
 * JUNCTION: the water flows left → right.
 *
 *   [ upstream A ] ─┐
 *                   ├─►  [ THIS DEVICE ]  ─►  [ downstream X ]
 *   [ upstream B ] ─┘                     ─►  [ downstream Y ]
 *
 * Mike, 2026-08-28: "the current flows from left to right ... if there are 2
 * nodes that feed in to one node then display those on top of each other to
 * make it clear what node is the intersection, and we should only be able to
 * jump from one node to a joining node that is adjacent".
 *
 * So: the devices that feed INTO this one are stacked on the left, the ones
 * this device feeds INTO are stacked on the right, and Open exists only on
 * those adjacent cards. The far end of the chain (the head) is one line of
 * text below the diagram, no buttons.
 *
 * ONE SAVE ("there are alot of save, we should use a single save button").
 * Nothing on this card writes by itself: upstream links are edited on their
 * cards and written by the form's Save; a downstream link is STAGED here and
 * written by that same Save (which then also updates the other device,
 * adding this one to its upstreams); Open only navigates, and only once
 * there is nothing unsaved. The "Save will:" list above the Save button names
 * every device Save touches and what changes on it.
 */

const HELP = {
  addUpstream:
    'Upstream is where the water flows FROM. Add a Stage device whose water arrives here. Several can feed in (a confluence): each link has its own flow ' +
    'speed and travel time, and the wave that arrives first starts this gauge. Adding the first upstream copies its ' +
    'scenario — a chain runs one scenario. Written when you press Save.',
  linkSpeed:
    'The speed of the water on this link, in miles per hour. Travel time = straight-line distance ÷ speed — an estimate.',
  downstream:
    'Downstream is where the water flows TO. Add a Stage device that this one feeds into. Staged here and written when you press Save: Save then also updates that ' +
    'device — this device is added to its upstreams (a second tributary when it already has one) and it takes this ' +
    'scenario. Its own Enabled switch is left as it is.',
  downstreamSpeed: 'The speed of the water from this device to that one, in miles per hour.',
};

type DeviceRef = NonNullable<FloodSimSettingsProps['device']>;

interface WaterwaySectionProps {
  sim: SimSettings | null | undefined;
  onChange: (sim: SimSettings) => void;
  device?: FloodSimSettingsProps['device'];
  hasUnsavedChanges?: boolean;
  onOpenDevice?: (deviceId: number) => void;
  pendingDownstream?: PendingDownstreamLink[];
  onStageDownstream?: (downstreamId: number, speedMph: number) => void;
  onUnstageDownstream?: (downstreamId: number) => void;
}

/**
 * The jump button on an adjacent card. Upstream — where the water comes from
 * — is cyan with the arrow first; downstream — where it goes — is orange, the
 * map's "next hop" colour, with the arrow last. Disabled while anything is
 * unsaved: Open never saves.
 */
const OPEN_STYLE = {
  upstream: { variant: 'info', label: '◀ Open' },
  downstream: { variant: 'warning', label: 'Open ▶' },
} as const;

function OpenButton({
  target,
  unsaved,
  onOpen,
  direction,
}: {
  target: DeviceLike;
  unsaved: boolean;
  onOpen?: (id: number) => void;
  direction: keyof typeof OPEN_STYLE;
}) {
  if (!onOpen || target.id == null) return null;
  const s = OPEN_STYLE[direction];
  return (
    <Button
      size="sm"
      variant={s.variant}
      className="py-0 px-2 fw-semibold text-dark text-nowrap"
      disabled={unsaved}
      title={
        unsaved
          ? `Save your changes first, then open ${deviceLabel(target)}`
          : `Open ${deviceLabel(target)} in this window`
      }
      onClick={() => onOpen(target.id as number)}
    >
      {s.label}
    </Button>
  );
}

const Arrow = () => (
  <div className="text-secondary fs-3 px-1" aria-hidden="true">
    →
  </div>
);

export default function WaterwaySection({
  sim,
  onChange,
  device,
  hasUnsavedChanges = false,
  onOpenDevice,
  pendingDownstream = [],
  onStageDownstream,
  onUnstageDownstream,
}: WaterwaySectionProps) {
  const { data: connectedDevices = [] } = useGetConnectedDevicesQuery(undefined);
  const stageDevices = (connectedDevices as DeviceLike[]).filter(isStageDevice);

  // This device as the FORM knows it — an unsaved link included — so the
  // diagram shows what will be saved. Everything else comes from the list,
  // which carries each device's saved routing.
  const listed = device ? stageDevices.find((d) => d.id === device.id) : undefined;
  const self: DeviceLike | null = device
    ? {
        ...(listed ?? (device as DeviceRef as DeviceLike)),
        device_settings: { sim: sim ?? undefined },
      }
    : null;
  const selfName = device ? deviceLabel(device as DeviceRef as DeviceLike) : 'this device';
  const lookup = (id: number): DeviceLike | undefined =>
    self && id === self.id ? self : stageDevices.find((d) => d.id === id);

  const routing = sim?.routing ?? null;
  const links = self ? upstreamLinksOf(self, lookup) : [];
  const paths = self && links.length > 0 ? walkUpstreamPaths(self, lookup) : [];
  const earliest = self && links.length > 0 ? walkUpstream(self, lookup) : null;
  const ancestors = ancestorsOf(self, lookup);
  const below = descendantsOf(self, stageDevices);
  const downstream = downstreamOf(self, stageDevices);

  // Which links the server already has — a link added in the form is "not saved yet" until Save.
  const savedUpstreamIds = new Set(upstreamLinksOf(listed).map((l) => l.uid));
  const linkSaved = (uid: number | undefined) => uid != null && savedUpstreamIds.has(uid);

  // Every other Stage device appears in both pickers; the ones that cannot be
  // chosen are greyed WITH THE REASON, so a missing name is never a mystery.
  const others = stageDevices.filter((d) => d.id != null && d.id !== device?.id);
  const upstreamReason = (d: DeviceLike): string | null => {
    if (links.some((l) => l.uid === d.id)) return 'already feeds into this device';
    if (below.has(d.id as number))
      return 'is BELOW this device — it cannot also feed into it (a loop); remove that link first';
    if (pendingDownstream.some((p) => p.id === d.id)) return 'staged as downstream of this device';
    return null;
  };
  const downstreamReason = (d: DeviceLike): string | null => {
    if (downstream.some((x) => x.id === d.id)) return 'this device already feeds into it';
    if (pendingDownstream.some((p) => p.id === d.id)) return 'already staged';
    if (ancestors.has(d.id as number))
      return 'is ABOVE this device — this device cannot also feed into it (a loop)';
    return null;
  };
  const upstreamCandidates = others.filter((d) => upstreamReason(d) === null);
  const downstreamCandidates = others.filter((d) => downstreamReason(d) === null);
  const pickerHint = (count: number, what: string) =>
    count === 0 && others.length > 0
      ? `No device can be ${what} right now — the greyed entries say why.`
      : null;

  const base = () => sim as SimSettings;

  const onAddUpstream = (uid: number) => {
    const up = lookup(uid);
    if (!up || !self) return;
    const built = buildLink({ upstream: up, downstream: self, speedMph: DEFAULT_FLOW_SPEED_MPH });
    const next = addUpstream(routing, built.link, self, lookup);
    // A chain runs ONE scenario: the FIRST upstream's scenario comes along, when it has one.
    const upSim = up.device_settings?.sim as Partial<SimSettings> | undefined;
    const sharedScenario =
      links.length === 0 && upSim?.sim_type === 'scenario' && upSim.scenario_name
        ? { scenario_name: upSim.scenario_name, scenario_params: upSim.scenario_params ?? {} }
        : {};
    onChange({ ...base(), ...sharedScenario, routing: next });
  };
  const onRemoveUpstream = (uid: number) => {
    if (!self) return;
    onChange({ ...base(), routing: removeUpstream(routing, uid, self, lookup) });
  };
  const onSetSpeed = (uid: number, speedMph: number) => {
    if (!self || !(speedMph > 0)) return;
    onChange({ ...base(), routing: setUpstreamSpeed(routing, uid, speedMph, self, lookup) });
  };

  // ── Stage a downstream link (written by Save) ──
  const [pendingId, setPendingId] = useState<string>('');
  const [linkSpeed, setLinkSpeed] = useState<number>(DEFAULT_FLOW_SPEED_MPH);
  useEffect(() => {
    setPendingId('');
    setLinkSpeed(DEFAULT_FLOW_SPEED_MPH);
  }, [device?.id]);
  const pending = pendingId ? (lookup(Number(pendingId)) ?? null) : null;
  const pendingMiles = pending && self ? deviceDistanceMiles(self, pending) : null;
  const pendingMinutes = travelMinutes(pendingMiles, linkSpeed);
  const pendingHasUpstreams = pending ? upstreamLinksOf(pending).length : 0;
  const stage = () => {
    if (!pending?.id || !onStageDownstream) return;
    onStageDownstream(pending.id, linkSpeed);
    setPendingId('');
  };

  const confluence = describeConfluence(paths);
  const cardClass = `${styles['waterway-node']} rounded px-2 py-2 d-flex flex-wrap align-items-center gap-2`;

  return (
    <SettingsSection
      title="Waterway routing"
      description="Connect adjacent Stage devices and estimate how a demo flood moves downstream."
    >
      {/* Abundantly clear: estimates from simple arithmetic, a simulation tool only. */}
      <div className="small text-warning border border-warning rounded px-2 py-1" role="note">
        <strong>Estimates only.</strong> {SIMULATION_DISCLAIMER}
      </div>
      <div className="small text-theme-muted">
        The water flows <strong>left → right</strong>: <strong>upstream</strong> — where the water
        flows <em>from</em> — on the left, this device in the middle, <strong>downstream</strong> —
        where the water flows <em>to</em> — on the right. Nothing here writes on its own — every
        change, including a staged downstream link, is written by the <strong>Save</strong>
        button at the bottom; the list above Save says exactly what it will do.
      </div>

      {/* ── The junction: upstream stack → THIS DEVICE → downstream stack ── */}
      <div className={styles['waterway-scroll']}>
        <div className={`${styles['waterway-flow']} small`}>
          {/* upstream stack */}
          <div className={`${styles['waterway-column']} d-flex flex-column gap-2`}>
            {links.length === 0 ? (
              <div className={`${cardClass} border-secondary text-secondary`}>
                no upstream — nothing flows in; the water starts here
              </div>
            ) : (
              links.map((l) => {
                const up = lookup(l.uid);
                const saved = linkSaved(l.uid);
                return (
                  <div
                    key={l.uid}
                    className={`${cardClass} ${saved ? 'border-info' : 'border-warning'}`}
                  >
                    {up && saved ? (
                      <OpenButton
                        target={up}
                        unsaved={hasUnsavedChanges}
                        onOpen={onOpenDevice}
                        direction="upstream"
                      />
                    ) : null}
                    <span>
                      <strong>{up ? deviceLabel(up) : `device ${l.uid} (not in the list)`}</strong>
                      <span className="text-secondary">
                        {` · ${Math.round(l.travelMinutes)} min`}
                        {l.distanceMiles != null
                          ? ` · ${Math.round(l.distanceMiles * 10) / 10} mi`
                          : ' · no coordinates'}
                      </span>
                      {!saved ? <span className="text-warning"> · not saved yet</span> : null}
                    </span>
                    <label
                      className="d-flex align-items-center gap-1 text-nowrap"
                      title={HELP.linkSpeed}
                    >
                      <span className="text-secondary">mph</span>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        style={{ width: 72 }}
                        min={0.1}
                        step={0.1}
                        value={l.flowSpeedMph ?? DEFAULT_FLOW_SPEED_MPH}
                        onChange={(e) => onSetSpeed(l.uid, parseFloat(e.target.value))}
                      />
                    </label>
                    <Button
                      size="sm"
                      variant="outline-light"
                      className="py-0 px-2"
                      title={`Remove the link from ${up ? deviceLabel(up) : l.uid} (written when you press Save)`}
                      onClick={() => onRemoveUpstream(l.uid)}
                    >
                      ✕
                    </Button>
                  </div>
                );
              })
            )}
          </div>

          <Arrow />

          {/* this device */}
          <div
            className={`${styles['current-device']} border border-warning rounded px-3 py-2 text-center`}
          >
            <div className="fw-semibold">{selfName}</div>
            <div className="text-secondary">this device</div>
            <div className="text-secondary">← from · to →</div>
            {links.length > 1 ? <div className="text-info">{links.length} feed in here</div> : null}
          </div>

          <Arrow />

          {/* downstream stack */}
          <div className={`${styles['waterway-column']} d-flex flex-column gap-2`}>
            {downstream.length === 0 && pendingDownstream.length === 0 ? (
              <div className={`${cardClass} border-secondary text-secondary`}>
                no downstream — the water flows to nothing; it ends here
              </div>
            ) : null}
            {downstream.map((d) => {
              const fromHere = self ? upstreamLinksOf(d).find((l) => l.uid === self.id) : null;
              const others = upstreamLinksOf(d).length - 1;
              return (
                <div key={String(d.id)} className={`${cardClass} border-warning`}>
                  <span>
                    <strong>{deviceLabel(d)}</strong>
                    <span className="text-secondary">
                      {fromHere ? ` · ${Math.round(fromHere.travelMinutes)} min` : ''}
                      {others > 0 ? ` · ${others} other${others === 1 ? '' : 's'} feed in too` : ''}
                    </span>
                  </span>
                  <OpenButton
                    target={d}
                    unsaved={hasUnsavedChanges}
                    onOpen={onOpenDevice}
                    direction="downstream"
                  />
                </div>
              );
            })}
            {pendingDownstream.map((p) => {
              const d = lookup(p.id);
              const miles = d && self ? deviceDistanceMiles(self, d) : null;
              const min = travelMinutes(miles, p.speedMph);
              return (
                <div
                  key={`pending:${p.id}`}
                  className={`${cardClass} border-warning`}
                  style={{ borderStyle: 'dashed' }}
                >
                  <span>
                    <strong>{d ? deviceLabel(d) : `device ${p.id}`}</strong>
                    <span className="text-secondary">
                      {min != null
                        ? ` · ${Math.round(min)} min at ${p.speedMph} mph`
                        : ' · no coordinates'}
                    </span>
                    <span className="text-warning"> · staged, written by Save</span>
                  </span>
                  {onUnstageDownstream ? (
                    <Button
                      size="sm"
                      variant="outline-light"
                      className="py-0 px-2"
                      title="Unstage"
                      onClick={() => onUnstageDownstream(p.id)}
                    >
                      ✕
                    </Button>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* The far end, in words — no buttons: only adjacent devices can be opened */}
      {earliest && earliest.hops.length > 0 ? (
        <div className="small text-secondary">
          {earliest.hops.length === 1
            ? `${deviceLabel(earliest.head)} is the head of this chain.`
            : `The chain starts at ${deviceLabel(earliest.head)}: ${describeWalk(earliest)}.`}
          {confluence ? ` ${confluence}.` : ''}
          {' (estimate)'}
        </div>
      ) : null}

      {/* ── Add an upstream device (left side) ── */}
      <FloodSettingsField
        variant="select"
        label={
          links.length === 0
            ? 'Add an upstream device (where the water flows from)'
            : 'Add another upstream device (where the water flows from)'
        }
        help={links.length === 0 ? FIELD_HELP.routing_upstream : HELP.addUpstream}
        value=""
        onChange={(value) => {
          if (value !== '') onAddUpstream(Number(value));
        }}
        placeholder="Choose the device the water arrives here from…"
        options={others.map((d) => {
          const reason = upstreamReason(d);
          const w = walkUpstream(d, lookup);
          return {
            value: String(d.id),
            isDisabled: reason !== null,
            label: `${deviceLabel(d)}${
              reason
                ? ` — ${reason}`
                : w.hops.length > 0
                  ? ` — #${w.position} below ${deviceLabel(w.head)}`
                  : downstreamOf(d, stageDevices).length > 0
                    ? ' — head of a chain'
                    : ''
            }`,
          };
        })}
      />
      {pickerHint(upstreamCandidates.length, 'added upstream') ? (
        <div className="small text-secondary">
          {pickerHint(upstreamCandidates.length, 'added upstream')}
        </div>
      ) : null}

      {/* ── Add a downstream device (right side), staged for Save ── */}
      {onStageDownstream ? (
        <>
          <FloodSettingsField
            variant="select"
            label="Add a downstream device (where the water flows to)"
            help={HELP.downstream}
            value={pendingId}
            onChange={setPendingId}
            placeholder="Choose the device this one feeds into…"
            options={others.map((d) => {
              const reason = downstreamReason(d);
              const n = upstreamLinksOf(d).length;
              return {
                value: String(d.id),
                isDisabled: reason !== null,
                label: `${deviceLabel(d)}${
                  reason
                    ? ` — ${reason}`
                    : n > 0
                      ? ` — already fed by ${n} device${n === 1 ? '' : 's'}; this one joins them`
                      : ''
                }`,
              };
            })}
          />
          {pickerHint(downstreamCandidates.length, 'added downstream') ? (
            <div className="small text-secondary">
              {pickerHint(downstreamCandidates.length, 'added downstream')}
            </div>
          ) : null}
          {pending ? (
            <>
              <FloodSettingsField
                label="Flow speed to it (mph)"
                help={HELP.downstreamSpeed}
                type="number"
                min={0.1}
                step={0.1}
                value={linkSpeed}
                onChange={(e) => setLinkSpeed(parseFloat(e.target.value))}
              />
              <div className="small d-flex flex-wrap align-items-center gap-2">
                <span className="text-secondary">
                  {pendingMiles == null
                    ? 'No coordinates on one of the two — it would fire together with this device.'
                    : `${Math.round(pendingMiles * 10) / 10} mi (straight line) · fires about ${Math.round(pendingMinutes ?? 0)} min after this device (estimate)`}
                  {pendingHasUpstreams > 0
                    ? ` · it already has ${pendingHasUpstreams} upstream${pendingHasUpstreams === 1 ? '' : 's'} — the earlier wave starts it`
                    : ''}
                </span>
                <Button
                  size="sm"
                  variant="warning"
                  onClick={stage}
                  title="Stage the link — it is written when you press Save"
                >
                  Stage {deviceLabel(pending)} as downstream
                </Button>
              </div>
            </>
          ) : null}
        </>
      ) : null}
    </SettingsSection>
  );
}
